import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProjectSettings, TtsVoice } from '@simultrain/shared';
import { TTS_VOICES } from '@simultrain/shared';
import { api } from '../api/client.js';

export function PlatformSettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ProjectSettings | null>(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [voice, setVoice] = useState<TtsVoice>('alloy');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getPlatformSettings().then((s) => {
      setSettings(s);
      setVoice(s.ttsVoice);
    });
  }, []);

  async function saveKey(e: React.FormEvent) {
    e.preventDefault();
    if (!keyDraft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.patchPlatformSettings({ openaiKey: keyDraft.trim() });
      setSettings(updated);
      setKeyDraft('');
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeKey() {
    if (!window.confirm(t('settings.removeKeyConfirm'))) return;
    try {
      setSettings(await api.patchPlatformSettings({ openaiKey: null }));
    } catch (err) {
      setError(String(err));
    }
  }

  async function changeVoice(v: TtsVoice) {
    setVoice(v);
    try {
      setSettings(await api.patchPlatformSettings({ ttsVoice: v }));
    } catch (err) {
      setError(String(err));
    }
  }

  if (!settings) return <div className="empty">…</div>;

  return (
    <div style={{ maxWidth: 560 }}>
      <Link to="/projects" className="muted" style={{ fontSize: 13 }}>
        ← {t('projects.title')}
      </Link>
      <h2>{t('settings.platformTitle')}</h2>
      <p className="muted" style={{ fontSize: 13 }}>{t('settings.platformHint')}</p>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{t('settings.ttsSection')}</h3>
        <p className="muted" style={{ fontSize: 13 }}>{t('settings.ttsHint')}</p>

        <div style={{ marginBottom: 12 }}>
          <label>{t('settings.openaiKeyStatus')}</label>
          <div className="row" style={{ marginBottom: 8 }}>
            <span className="muted">
              {settings.hasOpenaiKey ? `✓ ${t('settings.keySet')}` : `— ${t('settings.keyNotSet')}`}
            </span>
            {settings.hasOpenaiKey && (
              <button type="button" className="danger" style={{ fontSize: 12, padding: '4px 10px' }} onClick={removeKey}>
                {t('settings.removeKey')}
              </button>
            )}
          </div>
        </div>

        <form onSubmit={saveKey}>
          <label>{settings.hasOpenaiKey ? t('settings.replaceKey') : t('settings.openaiKey')}</label>
          <input
            type="password"
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder="sk-..."
            autoComplete="off"
          />
          <button type="submit" disabled={saving || !keyDraft.trim()} style={{ marginTop: 12 }}>
            {savedFlash ? t('steps.saved') : t('steps.save')}
          </button>
        </form>

        <div style={{ marginTop: 16 }}>
          <label>{t('settings.ttsVoice')}</label>
          <select value={voice} onChange={(e) => void changeVoice(e.target.value as TtsVoice)}>
            {TTS_VOICES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
